/**
 * Laying a compiled Dispatch state over a view, and a patch over an entry: the one place the two shapes meet.
 *
 * {@link resolveDispatchView} turns a definition and the state over it into the effective view the page renders from,
 * so no call site lays state over a definition by hand. {@link layViewStatePatch} is the cascade's write rule for a
 * state entry, so the web's local mirror lays a patch the way the server will store it.
 * {@link resolveDispatchPageSettings} does the same for the page's settings. Pure and browser-safe.
 */

import type { FilterNode } from "@storyboard/driver-query/predicate";
import { layConfigPatch, REPLACE_NOTHING } from "@storyboard/config-cascade/cascade";
import type {
  DispatchAction,
  DispatchColumn,
  DispatchControl,
  DispatchControlValue,
  DispatchField,
  DispatchGrain,
  DispatchKeymap,
  DispatchNone,
  DispatchPageSettings,
  DispatchSort,
  DispatchViewDefinition,
  DispatchViewState,
} from "./view";
import { DEFAULT_DISPATCH_KEYMAP, DISPATCH_ACTIONS, NONE, normalizeKeyBinding } from "./view";
import type { RequiresAcknowledgement } from "./requiresAcknowledgement";
import { DEFAULT_REQUIRES_ACKNOWLEDGEMENT } from "./requiresAcknowledgement";

/**
 * The state a view's controls start in when nothing says otherwise: each control's `default`, else off.
 */
export function defaultControlValues(controls: readonly DispatchControl[]): Record<string, DispatchControlValue> {
  const values: Record<string, DispatchControlValue> = {};
  for (const control of controls) {
    values[control.id] = control.default ?? inactiveControlValue(control);
  }
  return values;
}

/**
 * The value that switches a control off: `false`, no values, or no time span.
 */
export function inactiveControlValue(control: DispatchControl): DispatchControlValue {
  return control.kind === "toggle" ? false : control.kind === "values" ? [] : 0;
}

/**
 * Every control switched off (see {@link inactiveControlValue}), keyed by id: what "Clear all" writes.
 */
export function inactiveControlValues(controls: readonly DispatchControl[]): Record<string, DispatchControlValue> {
  return Object.fromEntries(controls.map((control) => [control.id, inactiveControlValue(control)]));
}

/**
 * The column ids a view pins, in the view's order: what its layout pins before any state says otherwise.
 */
export function pinnedColumnKeys(definition: DispatchViewDefinition): string[] {
  return definition.layout.columns.filter((column) => column.pinned === true).map((column) => column.field);
}

/**
 * The query in force: the view's with the state's values laid over it.
 */
export type ResolvedDispatchQuery = {
  controls: DispatchControl[];
  /** Every control's value, defaulted from the view then overridden by the state. */
  controlValues: Record<string, DispatchControlValue>;
  search: DispatchField[];
  /** Every condition in force; a row must meet them all. Empty when nothing narrows. */
  adhoc: FilterNode[];
  sort: DispatchSort | null;
  groupBy: DispatchField | null;
  collapsedGroups: readonly string[];
};

/**
 * The table in force: the offered columns and the maps the table keeps by column id.
 */
export type ResolvedDispatchTableLayout = {
  kind: "table";
  /** The columns offered: the view's first, then every other field the member may switch on. */
  columns: DispatchColumn[];
  /** Shown for the view's columns and hidden for the rest, then the state's choices. */
  visibility: Record<string, boolean>;
  order: string[];
  sizing: Record<string, number>;
  /** The state's pinning, else the columns the view pins. */
  pinning: { left: string[] };
};

/**
 * Which parts of the view the compiled state changes, for the strip's chips.
 *
 * Read off the result rather than the state where the view has a value to compare with: a grain, sort, grouping,
 * control, visibility, or pinning the state sets to the view's own changes nothing, and a key the state leaves out
 * changes nothing either. A column order or sizing counts as a change whenever the state carries one, since the view
 * has no order or sizing of its own to compare against.
 */
export type DispatchStateOverrides = {
  grain: boolean;
  /** The rows sort differently from the view, no sort where the view has one included. */
  sort: boolean;
  groupBy: boolean;
  adhoc: boolean;
  controls: boolean;
  /** Visibility or pinning differ from the view's, or the state carries any column order or sizing. */
  layout: boolean;
  /** The person's Requires Acknowledgement list names different things from the view's. */
  requiresAcknowledgement: boolean;
};

/**
 * The view the page renders from: definition and state already reconciled.
 */
export type ResolvedDispatchView = {
  id: string;
  label: string;
  grain: DispatchGrain;
  query: ResolvedDispatchQuery;
  layout: ResolvedDispatchTableLayout;
  /** What the person is asked to acknowledge: theirs, else the view's, else `DEFAULT_REQUIRES_ACKNOWLEDGEMENT`. */
  requiresAcknowledgement: RequiresAcknowledgement;
  overrides: DispatchStateOverrides;
  /** Whether the state changes anything at all: what Reset would drop. */
  personalized: boolean;
};

/**
 * A sparse change to a view's state: the keys that changed, `null` for an override taken back.
 *
 * The same shape as the state, since the cascade stores `null` and reads it as "inherit from the layer beneath".
 */
export type DispatchViewStatePatch = DispatchViewState;

/**
 * The entries of a record whose value is set, dropping the `null` ones that inherit.
 */
function present<T>(record: Record<string, T | null> | null | undefined): Record<string, T> {
  const kept: Record<string, T> = {};
  for (const [key, value] of Object.entries(record ?? {})) {
    if (value !== null) {
      kept[key] = value;
    }
  }
  return kept;
}

/**
 * Lays a compiled view state over its definition.
 *
 * The null rule, once: inside the state an absent or `null` key inherits the view's value, `"none"` on the sort or
 * grouping means none (in the view too), an empty conditions list means none. Control values default from the view's
 * controls, then the state's win. Visibility is true for the view's columns and false for every other offered column,
 * then the state's; pinning is the state's when it says, else the columns the view pins. A Requires Acknowledgement
 * list in the state replaces the view's whole, as any array does.
 *
 * @param definition - The view.
 * @param state - The compiled entry laid over it, or none.
 * @param offeredColumns - Every column the table may show, the view's first; defaults to the view's own. The web
 *   supplies the wider set, since only it knows the workspace's tags.
 */
export function resolveDispatchView(
  definition: DispatchViewDefinition,
  state: DispatchViewState | null | undefined,
  offeredColumns: readonly DispatchColumn[] = definition.layout.columns,
): ResolvedDispatchView {
  const query = state?.query ?? {};
  const columns = state?.layout?.columns ?? {};
  const viewGrain = definition.grain ?? "driver";
  const viewSort = noneToNull(definition.query.sort);
  const viewGroupBy = noneToNull(definition.query.groupBy);
  const viewPinned = pinnedColumnKeys(definition);
  const viewColumns = new Set(definition.layout.columns.map((column) => column.field));

  const viewVisibility: Record<string, boolean> = {};
  for (const column of offeredColumns) {
    viewVisibility[column.field] = viewColumns.has(column.field);
  }
  const visibility = { ...viewVisibility, ...present(columns.visibility) };
  const defaults = defaultControlValues(definition.query.controls);
  const controlValues = { ...defaults, ...present(query.controls) };
  const grain = state?.grain ?? viewGrain;
  const adhoc = query.adhoc ?? [];
  const sort = query.sort === NONE ? null : (query.sort ?? viewSort);
  const groupBy = query.groupBy === NONE ? null : (query.groupBy ?? viewGroupBy);
  const collapsedGroups = query.collapsedGroups ?? [];
  const order = columns.order ?? [];
  const sizing = present(columns.sizing);
  const pinning = columns.pinning ?? { left: viewPinned };
  const viewRequires = definition.requiresAcknowledgement ?? DEFAULT_REQUIRES_ACKNOWLEDGEMENT;
  const requiresAcknowledgement = state?.requiresAcknowledgement ?? viewRequires;

  const overrides: DispatchStateOverrides = {
    grain: grain !== viewGrain,
    sort: !sameSort(sort, viewSort),
    groupBy: groupBy !== viewGroupBy,
    adhoc: adhoc.length > 0,
    controls: Object.entries(controlValues).some(([id, value]) => {
      const fallback = defaults[id];
      return fallback !== undefined && !sameControlValue(value, fallback);
    }),
    layout:
      Object.entries(visibility).some(([key, shown]) => shown !== viewVisibility[key]) ||
      order.length > 0 ||
      Object.keys(sizing).length > 0 ||
      !sameList(pinning.left, viewPinned),
    requiresAcknowledgement: !sameSet(requiresAcknowledgement.include, viewRequires.include),
  };

  return {
    id: definition.id,
    label: definition.label,
    grain,
    query: {
      controls: definition.query.controls,
      controlValues,
      search: definition.query.search,
      adhoc,
      sort,
      groupBy,
      collapsedGroups,
    },
    layout: { kind: "table", columns: [...offeredColumns], visibility, order, sizing, pinning },
    requiresAcknowledgement,
    overrides,
    personalized: Object.values(overrides).some(Boolean) || collapsedGroups.length > 0,
  };
}

/**
 * Lays a patch over a stored state entry the way the cascade's write path does.
 *
 * {@link layConfigPatch} typed to the entry: objects layer key by key, arrays and scalars replace, and a `null` is
 * kept as the inherit it is, so the result is what the server stores and what a later patch lays over. The web mirrors
 * its own PATCH with this and accumulates two patches into one body with it.
 *
 * @param base - The entry as stored, or none.
 * @param patch - The keys that changed.
 */
export function layViewStatePatch(
  base: DispatchViewState | null | undefined,
  patch: DispatchViewStatePatch,
): DispatchViewState {
  return layConfigPatch(REPLACE_NOTHING, base ?? {}, patch);
}

/**
 * A view's `"none"`, or an absent value, as the null the resolved view carries.
 */
function noneToNull<T>(value: T | DispatchNone | undefined): T | null {
  return value === undefined || value === NONE ? null : value;
}

function sameSort(a: DispatchSort | null, b: DispatchSort | null): boolean {
  return a === null || b === null ? a === b : a.field === b.field && a.direction === b.direction;
}

function sameControlValue(a: DispatchControlValue, b: DispatchControlValue): boolean {
  // A values control is a set: the filter reads membership, so the order the values were ticked in is nothing.
  return Array.isArray(a) && Array.isArray(b) ? sameSet(a, b) : a === b;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const sortedB = [...b].sort();
  return a.length === b.length && [...a].sort().every((value, index) => value === sortedB[index]);
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * The page settings in force: every action bound or unbound, the shortcut bar decided.
 */
export type ResolvedDispatchPageSettings = {
  /** One binding per action; an empty string is an action left unbound by a collision. */
  keymap: Record<DispatchAction, string>;
  showShortcuts: boolean;
  /** The slug of the tag holding drivers' phone numbers; `null` when the workspace names none. */
  phoneTagSlug: string | null;
};

const actionSet: ReadonlySet<string> = new Set(DISPATCH_ACTIONS);

/**
 * The page's settings over the code defaults.
 *
 * The keymap's overrides lie on {@link DEFAULT_DISPATCH_KEYMAP}, with any key for an action that no longer exists
 * dropped rather than failing the document. When two actions end up on one key, as a member's binding merged over the
 * workspace's can, the action earlier in {@link DISPATCH_ACTIONS} keeps the key and the other is unbound: never an
 * error, and never two actions firing on one keystroke. The shortcut bar shows unless the settings say otherwise. A
 * blank phone tag slug reads as none.
 *
 * @param settings - The page settings the cascade compiled, or none.
 * @param withheld - Actions the page does not offer, such as `task` while the workspace's task surface is off: each is
 *   left unbound and claims no key, so every other action resolves as if it did not exist. Empty to offer them all.
 */
export function resolveDispatchPageSettings(
  settings: DispatchPageSettings | null | undefined,
  withheld: ReadonlySet<DispatchAction>,
): ResolvedDispatchPageSettings {
  const overrides: DispatchKeymap = {};
  for (const [action, binding] of Object.entries(settings?.keymap ?? {})) {
    if (actionSet.has(action) && typeof binding === "string" && binding !== "") {
      overrides[action as DispatchAction] = binding;
    }
  }
  const keymap = { ...DEFAULT_DISPATCH_KEYMAP, ...overrides };
  const taken = new Set<string>();
  for (const action of DISPATCH_ACTIONS) {
    const key = normalizeKeyBinding(keymap[action]);
    if (withheld.has(action) || taken.has(key)) {
      keymap[action] = "";
    } else {
      taken.add(key);
    }
  }
  return { keymap, showShortcuts: settings?.showShortcuts ?? true, phoneTagSlug: settings?.phoneTagSlug || null };
}
